/**
 * useSelection
 * ────────────
 * Manages what's currently selected, highlighted, and multi-selected on the map.
 *
 * State owned:
 *   - selectedProject / selectedChallenge (the focused node)
 *   - highlightedIds[]  (IDs that glow — lineage chain, branch, etc.)
 *   - isMultiSelectMode  (shift-click mode active)
 *   - multiSelectedIds[] (IDs checked during multi-select)
 *
 * Also exposes the derived `multiSelectedProjects` array (objects, not just IDs).
 *
 * Place at: src/hooks/useSelection.js
 */

import { useState, useCallback, useMemo } from 'react';

export default function useSelection(projects = []) {
  // Focused node
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedChallenge, setSelectedChallenge] = useState(null);

  // Lineage / branch highlight
  const [highlightedIds, setHighlightedIds] = useState([]);

  // Multi-select
  const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);
  const [multiSelectedIds, setMultiSelectedIds] = useState([]);

  // ── Derived ──────────────────────────────────────────────────────────────

  const multiSelectedProjects = useMemo(() => {
    return projects.filter(p => multiSelectedIds.includes(p.id));
  }, [projects, multiSelectedIds]);

  // ── Actions ──────────────────────────────────────────────────────────────

  const enterMultiSelect = useCallback(() => {
    setIsMultiSelectMode(true);
  }, []);

  const exitMultiSelect = useCallback((keepHighlightedIds = null) => {
    setIsMultiSelectMode(false);
    setMultiSelectedIds([]);
    if (keepHighlightedIds) {
      setHighlightedIds(keepHighlightedIds);
    }
  }, []);

  const toggleMultiSelected = useCallback((projectId) => {
    setMultiSelectedIds(prev =>
      prev.includes(projectId)
        ? prev.filter(id => id !== projectId)
        : [...prev, projectId]
    );
  }, []);

  const clearMultiSelection = useCallback(() => {
    setMultiSelectedIds([]);
  }, []);

  /**
   * Full reset — deselect everything, clear highlights, exit multi-select.
   * Used on background click.
   */
  const clearAll = useCallback(() => {
    setSelectedProject(null);
    setSelectedChallenge(null);
    setHighlightedIds([]);
    setIsMultiSelectMode(false);
    setMultiSelectedIds([]);
  }, []);

  return {
    // State
    selectedProject,
    selectedChallenge,
    highlightedIds,
    isMultiSelectMode,
    multiSelectedIds,
    multiSelectedProjects,

    // Setters (for orchestration in Dashboard)
    setSelectedProject,
    setSelectedChallenge,
    setHighlightedIds,
    setMultiSelectedIds,

    // Actions
    enterMultiSelect,
    exitMultiSelect,
    toggleMultiSelected,
    clearMultiSelection,
    clearAll,
  };
}